<script setup lang="ts">
import { MAP_VIEW } from '@/constant/menu'
import { ROUTE_NAMES } from '@/router/routes'
import { useRouter, useRoute } from 'vue-router'
import { computed, defineAsyncComponent, onMounted } from 'vue'
import NavBarAvatar from '@/layouts/nav-bar/NavBarAvatar.vue'
import { useChatStore } from '@/stores/chat'
import IconLogo from '@/components/icons/IconLogo.vue'
import { useSettingStore } from '@/stores/setting'
import { storeToRefs } from 'pinia'
import IconBase from '@/components/base/IconBase.vue'
import IconChatBot from '@/components/icons/chat-bot/IconChatBot.vue'
import NavBarNotification from './NavBarNotification.vue'
import NavBarInfo from './NavBarInfo.vue'
import WorkspaceSelector from '@/components/WorkspaceSelector.vue'

// These three are only rendered on the map view (v-if="isMapview") but
// pull in mars3d/Cesium transitively — lazy-load them so the mars3d
// bundle isn't fetched eagerly by every page via NavBar.
const MapPlaceSearch = defineAsyncComponent(
  () => import('@/views/map/mars3d/components/MapPlaceSearch.vue'),
)
const ScreenRecordButton = defineAsyncComponent(
  () => import('@/views/map/mars3d/ScreenRecordButton.vue'),
)
const ReportingButton = defineAsyncComponent(() => import('@/views/map/mars3d/ReportingButton.vue'))
const props = defineProps({
  hasLogo: Boolean,
  hasChatbot: Boolean,
})

const router = useRouter()
const settingStore = useSettingStore()
const { currentMenu, menus } = storeToRefs(settingStore)
const handleLogoClick = () => {
  router.push(`/`)
}
const route = useRoute()
const sessionId = computed(() => route.query.conversationId as string)
onMounted(() => {
  if (sessionId.value) {
    chatStore.openChatBot(sessionId.value)
    // currentMenu.value = CHAT_ORCHESTRATION
  }
})
const chatStore = useChatStore()

const title = computed(() => {
  return menus.value.find((menu) => menu.value === currentMenu.value)?.title || ''
})

const isMapview = computed(
  () => currentMenu.value == MAP_VIEW && route.name === ROUTE_NAMES.WORKSPACE_DETAIL,
)
</script>

<template>
  <v-app-bar
    :elevation="0"
    height="56"
    class="tw:bg-s-d-s-dark-background-surface-secondary tw:border-b tw:border-b-s-d-s-dark-border-default-secondary tw:pl-16"
  >
    <template v-slot:prepend>
      <div v-if="hasLogo" @click="handleLogoClick">
        <IconLogo />
      </div>
      <div v-else class="tw:text-2 text-uppercase">
        {{ title }}
      </div>
    </template>
    <template v-slot:append>
      <div class="tw:flex tw:h-full">
        <div
          class="tw:flex tw:items-center tw:gap-(--sds-size-space-16)"
          :class="isMapview ? 'tw:pr-0' : 'tw:pr-16'"
        >
          <ScreenRecordButton v-if="isMapview" />
          <ReportingButton v-if="isMapview" />

          <WorkspaceSelector
            v-if="route.name !== ROUTE_NAMES.HOME && route.name !== ROUTE_NAMES.WORKSPACES"
          />

          <MapPlaceSearch v-if="isMapview" />
        </div>

        <div
          class="tw:flex tw:flex-row tw:items-center tw:gap-16 tw:px-16 tw:border-x tw:border-x-s-d-s-dark-border-default-secondary"
        >
          <IconBase
            v-if="false"
            :active="chatStore.chatBotActive"
            variant="subtle"
            :tooltip="'Chat Assistant'"
            @click="chatStore.openChatBot(undefined)"
          >
            <template #icon>
              <IconChatBot />
            </template>
          </IconBase>

          <NavBarNotification />

          <NavBarInfo />
        </div>
        <div class="tw:p-16 tw:flex tw:items-center">
          <NavBarAvatar />
        </div>
      </div>
    </template>
  </v-app-bar>
</template>

<style scoped>
:deep(.v-toolbar__append) {
  margin-inline: unset !important;
}
</style>
